/**
 * BatchStageCards Component
 *
 * Responsive grid of stage indicator cards showing batch progress details.
 * Each card shows a workflow stage with icon, count, and visual indicator.
 *
 * Design:
 * - Responsive 4-column grid (2 on mobile, 4 on desktop)
 * - Color-coded stages matching BatchProgressBar
 * - Active stage highlighted with ring indicator
 * - Zero counts shown in muted state
 * - Error card shows detailed breakdown on hover
 *
 * UX improvements (Phase 6.1 review):
 * - Increased touch targets (p-4) for warehouse usability
 * - Error breakdown tooltip for granular visibility
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { BatchProgress } from '../types';
import {
  Clock,
  Package,
  Droplet,
  Film,
  Flame,
  Hand,
  Box,
  PackageCheck,
  Truck,
  AlertTriangle,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface BatchStageCardsProps {
  progress: BatchProgress;
  className?: string;
}

type StageKey = 'pending' | 'picking' | 'pretreat' | 'filmPrinted' | 'pressed' | 'catching' | 'binning' | 'packing' | 'complete' | 'errors';

interface StageConfig {
  key: StageKey;
  labelKey: string;
  icon: typeof Clock;
  colorClass: string;
  bgClass: string;
  countKeys?: readonly (keyof BatchProgress)[];
}

/**
 * Stage configuration with icons and colors.
 * Matches stages from BatchProgressBar for consistency.
 */
const STAGE_CARDS: StageConfig[] = [
  {
    key: 'pending',
    labelKey: 'stages.pending',
    icon: Clock,
    colorClass: 'text-muted-foreground',
    bgClass: 'bg-muted/50',
  },
  {
    key: 'picking',
    labelKey: 'stages.picking',
    icon: Package,
    colorClass: 'text-blue-600 dark:text-blue-400',
    bgClass: 'bg-blue-50 dark:bg-blue-950/50',
    countKeys: ['labelPrinted', 'picked'],
  },
  {
    key: 'pretreat',
    labelKey: 'stages.pretreat',
    icon: Droplet,
    colorClass: 'text-purple-600 dark:text-purple-400',
    bgClass: 'bg-purple-50 dark:bg-purple-950/50',
    countKeys: ['inPretreat', 'pretreated'],
  },
  // Phase 50.01.5 — DTF stages (icon + colour, both DTG and DTF converge at
  // catching). Film (sky) + Flame (fuchsia / heat-press) — non-colliding with
  // the DTG icons (Droplet/Hand) and tones (purple/amber).
  {
    key: 'filmPrinted',
    labelKey: 'stages.filmPrinted',
    icon: Film,
    colorClass: 'text-sky-600 dark:text-sky-400',
    bgClass: 'bg-sky-50 dark:bg-sky-950/50',
    countKeys: ['filmPrinted'],
  },
  {
    key: 'pressed',
    labelKey: 'stages.pressed',
    icon: Flame,
    colorClass: 'text-fuchsia-600 dark:text-fuchsia-400',
    bgClass: 'bg-fuchsia-50 dark:bg-fuchsia-950/50',
    countKeys: ['pressed'],
  },
  {
    key: 'catching',
    labelKey: 'stages.catching',
    icon: Hand,
    colorClass: 'text-amber-600 dark:text-amber-400',
    bgClass: 'bg-amber-50 dark:bg-amber-950/50',
    countKeys: ['caught'],
  },
  {
    key: 'binning',
    labelKey: 'stages.binning',
    icon: Box,
    colorClass: 'text-orange-600 dark:text-orange-400',
    bgClass: 'bg-orange-50 dark:bg-orange-950/50',
    countKeys: ['inBin'],
  },
  {
    key: 'packing',
    labelKey: 'stages.packing',
    icon: PackageCheck,
    colorClass: 'text-emerald-600 dark:text-emerald-400',
    bgClass: 'bg-emerald-50 dark:bg-emerald-950/50',
    countKeys: ['readyToPack', 'released', 'packed'],
  },
  {
    key: 'complete',
    labelKey: 'stages.complete',
    icon: Truck,
    colorClass: 'text-green-600 dark:text-green-400',
    bgClass: 'bg-green-50 dark:bg-green-950/50',
    countKeys: ['shipped'],
  },
  {
    key: 'errors',
    labelKey: 'stages.errors',
    icon: AlertTriangle,
    colorClass: 'text-destructive',
    bgClass: 'bg-destructive/10',
    countKeys: ['errors'],
  },
];

/**
 * Calculate count for a stage based on its count keys.
 */
function getStageCount(progress: BatchProgress, stage: StageConfig): number {
  if (stage.countKeys) {
    return stage.countKeys.reduce(
      (sum, key) => sum + ((progress[key] as number) || 0),
      0
    );
  }
  return (progress[stage.key as keyof BatchProgress] as number) || 0;
}

/**
 * Format error breakdown for tooltip display
 */
function formatErrorBreakdown(progress: BatchProgress, t: (key: string, opts?: Record<string, unknown>) => string): string[] {
  const breakdown = progress.errorBreakdown;
  const lines: string[] = [];

  if (breakdown.inventoryError > 0) lines.push(t('stages.missingStock', { count: breakdown.inventoryError }));
  if (breakdown.papDamaged > 0) lines.push(t('stages.papDamaged', { count: breakdown.papDamaged }));
  if (breakdown.failed > 0) lines.push(t('stages.failed', { count: breakdown.failed }));
  if (breakdown.discarded > 0) lines.push(t('stages.discarded', { count: breakdown.discarded }));
  if (breakdown.cancelled > 0) lines.push(t('stages.cancelled', { count: breakdown.cancelled }));
  if (breakdown.cancelledReturned > 0) lines.push(t('stages.returned', { count: breakdown.cancelledReturned }));
  if (breakdown.orderBlocked > 0) lines.push(t('stages.blocked', { count: breakdown.orderBlocked }));

  return lines.length > 0 ? lines : [t('stages.noErrors')];
}

/**
 * Extended stage config with computed values
 */
type StageWithData = StageConfig & { count: number; isActive: boolean };

/**
 * Render a single stage card
 */
function StageCard({
  stage,
  progress,
}: {
  stage: StageWithData;
  progress: BatchProgress;
}) {
  const { t } = useTranslation('production');
  const Icon = stage.icon;
  const hasItems = stage.count > 0;
  const isErrorCard = stage.key === 'errors';

  const cardContent = (
    <Card
      className={cn(
        'transition-all duration-200',
        stage.isActive && 'ring-2 ring-primary ring-offset-2',
        !hasItems && 'opacity-50'
      )}
    >
      {/* p-4 for better touch targets on warehouse tablets */}
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          {/* Icon with background - increased size for visibility */}
          <div
            className={cn(
              'p-2 rounded-md',
              stage.bgClass
            )}
          >
            <Icon
              className={cn('h-5 w-5', stage.colorClass)}
              aria-hidden="true"
            />
          </div>

          {/* Label and count */}
          <div className="flex-1 min-w-0">
            <p className="text-sm text-muted-foreground truncate">
              {t(stage.labelKey)}
            </p>
            <p
              className={cn(
                'text-xl font-semibold leading-none',
                hasItems ? stage.colorClass : 'text-muted-foreground'
              )}
            >
              {stage.count}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // Wrap error card in tooltip showing breakdown
  if (isErrorCard && hasItems) {
    const breakdownLines = formatErrorBreakdown(progress, t);
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            {cardContent}
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <p className="font-medium mb-1">{t('stages.errorBreakdown')}</p>
            {breakdownLines.map((line, idx) => (
              <p key={idx} className="text-xs text-muted-foreground">{line}</p>
            ))}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return cardContent;
}

/**
 * BatchStageCards - Grid of workflow stage cards
 */
export function BatchStageCards({ progress, className }: BatchStageCardsProps) {
  const dominantStage = progress.dominantStage;

  // Calculate counts for each stage
  const stageData = useMemo(() => {
    return STAGE_CARDS.map((stage) => ({
      ...stage,
      count: getStageCount(progress, stage),
      isActive: stage.key === dominantStage,
    }));
  }, [progress, dominantStage]);

  return (
    <div
      className={cn(
        'grid grid-cols-2 sm:grid-cols-4 gap-4',
        className
      )}
    >
      {stageData.map((stage) => (
        <StageCard key={stage.key} stage={stage} progress={progress} />
      ))}
    </div>
  );
}

export default BatchStageCards;
